'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

interface HeroSlide {
  id: number;
  label: string;
  slideNum: string;
  title: string[];
  accentWord: string;
  subtitle: string;
  image: string;
  imageAlt: string;
  caption: string;
  captionNum: string;
  captionTitle: string;
}

const slides: HeroSlide[] = [
{
  id: 1,
  label: 'Welcome to UNOC',
  slideNum: '01 / 03',
  title: ["Powering Uganda's", ''],
  accentWord: 'Energy Future',
  subtitle: '10 years of driving Uganda\'s transformation in the petroleum sector — growing from a bold idea into a fully-fledged national oil company shaping the country\'s energy future.',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/12/WhatsApp-Image-2025-12-20-at-2.15.50-PM.jpeg',
  imageAlt: 'UNOC celebrated 10 years of driving Uganda\'s transformation in the petroleum sector at a major event',
  caption: '1. Upstream',
  captionNum: '01 / 03',
  captionTitle: 'Exploration and production at Lake Albert basin.'
},
{
  id: 2,
  label: 'Welcome to UNOC',
  slideNum: '02 / 03',
  title: ['Building East Africa\'s', ''],
  accentWord: 'Energy Pipeline',
  subtitle: 'Operating midstream infrastructure that connects Uganda\'s oil fields to global markets — including the world\'s longest electrically heated crude oil pipeline.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11d13c5e3-1778365791187.png",
  imageAlt: 'East African Crude Oil Pipeline EACOP infrastructure connecting Uganda to Tanzania for crude oil export',
  caption: '2. Pipeline',
  captionNum: '02 / 03',
  captionTitle: 'Cross-border crude transport at scale.'
},
{
  id: 3,
  label: 'Welcome to UNOC',
  slideNum: '03 / 03',
  title: ['Responsible ', ''],
  accentWord: 'Resource Development',
  subtitle: 'Balancing economic value with environmental stewardship across the petroleum value chain — from upstream exploration to community impact.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f51f9d40-1778365791056.png",
  imageAlt: 'Uganda Oil Refinery project at Kabaale Industrial Park Hoima District downstream processing facility',
  caption: '3. Downstream',
  captionNum: '03 / 03',
  captionTitle: 'Refining and processing for national energy security.'
}];


export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating]);

  const prev = useCallback(() => {
    goToSlide((current - 1 + slides.length) % slides.length);
  }, [current, goToSlide]);

  const next = useCallback(() => {
    goToSlide((current + 1) % slides.length);
  }, [current, goToSlide]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section id="home" aria-label="Hero" className="relative w-full bg-[#0F0F0F] overflow-hidden">
      <div className="h-[72px] md:h-[112px]" />
      <div className="relative h-[640px] sm:h-[700px] lg:h-[760px] w-full">
        {/* Background image */}
        <div
          className="absolute inset-0"
          style={{ opacity: 1, transition: 'opacity 0.6s ease' }}>
          
          <img
            src={slide.image}
            alt={slide.imageAlt}
            className="w-full h-full object-cover"
            style={{ transition: 'opacity 0.6s ease' }} />
          
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30" />
        </div>

        {/* Content */}
        <div className="relative h-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-col justify-center">
          <div
            className="max-w-3xl text-white"
            style={{
              opacity: isAnimating ? 0 : 1,
              transform: isAnimating ? 'translateY(10px)' : 'none',
              transition: 'opacity 0.5s ease, transform 0.5s ease'
            }}>
            
            <p className="text-sm sm:text-base text-white/80 mb-4 font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
              {slide.label}
            </p>
            <h1
              className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6"
              style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.025em' }}>
              
              {slide.title[0]}<br />
              <span className="text-[#CC0000]">{slide.accentWord}</span>
            </h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-xl mb-10" style={{ fontFamily: 'Inter, sans-serif' }}>
              {slide.subtitle}
            </p>
            <Link
              href="/projects"
              className="inline-flex items-center gap-2 bg-[#CC0000] text-white pl-6 pr-2 py-3 rounded-sm font-semibold hover:bg-[#AA0000] transition-colors group"
              style={{ fontFamily: 'Inter, sans-serif' }}>
              
              Explore Our Projects
              <span className="bg-white/15 group-hover:bg-white/25 p-2 rounded-sm transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7 7h10v10" />
                  <path d="M7 17 17 7" />
                </svg>
              </span>
            </Link>
          </div>

          {/* Slide controls */}
          <div className="absolute left-4 sm:left-6 lg:left-10 bottom-8 flex gap-2">
            <button
              aria-label="Previous slide"
              onClick={prev}
              className="h-12 w-12 bg-white text-[#0F0F0F] hover:bg-[#CC0000] hover:text-white transition-colors flex items-center justify-center rounded-sm">
              
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button
              aria-label="Next slide"
              onClick={next}
              className="h-12 w-12 bg-white text-[#0F0F0F] hover:bg-[#CC0000] hover:text-white transition-colors flex items-center justify-center rounded-sm">
              
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>

          {/* Slide caption - bottom right */}
          <div className="absolute right-4 sm:right-6 lg:right-10 bottom-8 bg-white/10 backdrop-blur-sm border border-white/20 p-4 max-w-[260px] hidden sm:block">
            <div className="flex items-center justify-between mb-2">
              <p className="font-mono text-[10px] text-white/70 tracking-[0.2em]">{slide.caption}</p>
              <p className="font-mono text-[10px] text-white/50 tracking-[0.15em]">{slide.captionNum}</p>
            </div>
            <h3 className="text-sm font-bold text-white leading-snug" style={{ fontFamily: 'Inter, sans-serif' }}>
              {slide.captionTitle}
            </h3>
          </div>
        </div>
      </div>
    </section>);

}